import { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp 
} from 'firebase/firestore';
import { db, loggedGetDocs } from '../firebase';
import { Expense } from '../types/expenseTypes';
import { useAuthContext } from '../context/AuthContext';

interface UseEnhancedExpensesOptions {
  pageSize?: number;
  enableRealtime?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
  filters?: {
    type?: string;
    unit?: string;
    costCenter?: string;
    category?: string;
    approvalStatus?: string;
  };
}

interface UseEnhancedExpensesReturn {
  expenses: Expense[];
  loading: boolean;
  error: string | null;
  hasNextPage: boolean;
  totalCount: number;
  loadNextPage: () => Promise<void>;
  /**
   * Manually trigger a refetch from Firestore and update local state
   */
  refresh: () => Promise<void>;
  filterData: (filters: any) => Expense[];
}

export const useEnhancedExpenses = (
  options: UseEnhancedExpensesOptions = {}
): UseEnhancedExpensesReturn => {
  const {
    pageSize = 100, // Increased from 25 to 100 for more data
    enableRealtime = true,
    dateRange,
    filters = {}
  } = options;

  const { roles, assignedUnits, user } = useAuthContext();
  const isAdmin = roles.includes('admin');

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Memoize stable values to prevent unnecessary re-renders
  const stableAssignedUnits = useMemo(() => assignedUnits, [JSON.stringify(assignedUnits)]);
  const stableFilters = useMemo(() => filters, [JSON.stringify(filters)]);
  const stableDateRange = useMemo(() => dateRange, [JSON.stringify(dateRange)]);

  // Build base query with RBAC filters
  const buildQuery = useCallback((startAfterDoc?: QueryDocumentSnapshot<DocumentData>) => {
    let baseQuery = collection(db, 'expenses');
    const constraints: any[] = [];

    // Apply RBAC: Non-admin users only see their assigned units
    if (!isAdmin) {
      if (stableAssignedUnits.length === 0) {
        // No assigned units means no access - return null to skip query
        return null;
      }
      
      // Add unit filter for assigned units
      constraints.push(where('unitId', 'in', stableAssignedUnits));
    }

    // Apply date range filter
    if (stableDateRange?.start) {
      constraints.push(where('date', '>=', Timestamp.fromDate(stableDateRange.start)));
    }
    if (stableDateRange?.end) {
      constraints.push(where('date', '<=', Timestamp.fromDate(stableDateRange.end)));
    }

    // Apply individual filters
    Object.entries(stableFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        constraints.push(where(key, '==', value));
      }
    });

    // Always order by creation date (newest first) for consistent pagination
    constraints.push(orderBy('createdAt', 'desc'));

    // Add pagination
    constraints.push(limit(pageSize));
    if (startAfterDoc) {
      constraints.push(startAfter(startAfterDoc));
    }

    return query(baseQuery, ...constraints);
  }, [isAdmin, stableAssignedUnits, stableDateRange, stableFilters, pageSize]);

  // Fetch expenses with pagination
  const fetchExpenses = useCallback(async (reset = false, startAfterDoc?: QueryDocumentSnapshot<DocumentData>) => {
    // Prevent simultaneous calls
    if (loading && !reset) return;
    
    try {
      setLoading(true);
      setError(null);

      const q = buildQuery(reset ? undefined : startAfterDoc);
      
      // If query is null (no access for non-admin with no assigned units), set empty results
      if (!q) {
        setExpenses([]);
        setLastDoc(null);
        setHasNextPage(false);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      const snapshot = await loggedGetDocs(q);

      const fetchedExpenses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        date: doc.data().date?.toDate?.() || new Date(),
      })) as Expense[];

      if (reset) {
        setExpenses(fetchedExpenses);
      } else {
        setExpenses(prev => [...prev, ...fetchedExpenses]);
      }

      // Update pagination
      const lastDocument = snapshot.docs[snapshot.docs.length - 1];
      setLastDoc(lastDocument || null);
      setHasNextPage(snapshot.docs.length === pageSize);
      
      if (reset) {
        setTotalCount(fetchedExpenses.length);
      }

    } catch (err) {
      console.error('❌ Error fetching expenses:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  }, [buildQuery, pageSize, loading]);

  // Load next page
  const loadNextPage = useCallback(async () => {
    if (!hasNextPage || loading) return;
    
    // Check if we have access to data
    const testQuery = buildQuery();
    if (!testQuery) return;
    
    await fetchExpenses(false, lastDoc || undefined);
  }, [hasNextPage, loading, fetchExpenses, buildQuery, lastDoc]);

  // Refresh data
  const refresh = useCallback(async () => {
    setLastDoc(null);
    await fetchExpenses(true);
  }, [fetchExpenses]);

  // Client-side filtering for additional filters not handled by Firestore
  const filterData = useCallback((clientFilters: any) => {
    return expenses.filter(expense => {
      // Apply unit access control - use unitId to match assignedUnits (which contains IDs)
      if (!isAdmin && !stableAssignedUnits.includes(expense.unitId)) {
        return false;
      }

      // Apply client-side filters
      if (clientFilters.search) {
        const searchLower = clientFilters.search.toLowerCase();
        const searchableFields = [
          expense.shop,
          expense.description,
          expense.name,
          expense.category,
          expense.costCenter,
        ];
        
        const matchesSearch = searchableFields.some(field => 
          field?.toLowerCase().includes(searchLower)
        );
        
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [expenses, isAdmin, stableAssignedUnits]);

  // Initial load
  useEffect(() => {
    if (isInitialized) return; // Prevent multiple initializations
    
    if (user && (isAdmin || stableAssignedUnits.length > 0)) {
      setLastDoc(null);
      fetchExpenses(true);
      setIsInitialized(true);
    }
  }, [user?.uid, isAdmin, stableAssignedUnits.length, fetchExpenses, isInitialized]);

  // Real-time updates (simplified and optimized)
  useEffect(() => {
    if (!enableRealtime || !user || !isInitialized) return;

    // For real-time updates, use a longer interval to prevent excessive calls
    const intervalId = setInterval(() => {
      if (!loading && isInitialized) {
        refresh();
      }
    }, 120000); // Refresh every 2 minutes (reduced frequency)

    return () => clearInterval(intervalId);
  }, [enableRealtime, user?.uid, loading, refresh, isInitialized]);

  return {
    expenses,
    loading,
    error,
    hasNextPage,
    totalCount,
    loadNextPage,
    refresh,
    filterData,
  };
};

// Helper hook for getting unique filter options
export const useExpenseFilterOptions = (expenses: Expense[]) => {
  return useMemo(() => {
    const types = Array.from(new Set(expenses.map(e => e.type))).map(type => ({
      label: type,
      value: type,
    }));

    const units = Array.from(new Set(expenses.map(e => e.unit))).map(unit => ({
      label: unit,
      value: unit,
    }));

    const costCenters = Array.from(new Set(expenses.map(e => e.costCenter))).map(cc => ({
      label: cc,
      value: cc,
    }));

    const categories = Array.from(new Set(expenses.map(e => e.category))).map(cat => ({
      label: cat,
      value: cat,
    }));

    const statuses = Array.from(new Set(expenses.map(e => e.approvalStatus || 'pending'))).map(status => ({
      label: status.charAt(0).toUpperCase() + status.slice(1),
      value: status,
    }));

    return {
      types,
      units,
      costCenters,
      categories,
      statuses,
    };
  }, [expenses]);
};
